// Scan de code-barres en direct à la caméra (`getUserMedia` + boucle de détection) — déplacé tel
// quel depuis main.ts, qui orchestre désormais la capture photo.
//
// La CAPTURE DE LA PHOTO, elle, ne passe PAS par ici : elle utilise
// `<input type="file" capture="environment">` (voir capture/ et main.ts). La capture native donne
// une meilleure photo (autofocus, HDR, flash du constructeur) et évite la gestion des permissions.
// Ce mode direct reste utile pour UN cas précis : viser un code-barres, où la boucle sur plusieurs
// images vaut mieux qu'un cliché unique.
import { createBarcodeScanner, type BarcodeScanner } from '../barcode/scanner.js';

export interface ScanElements {
  scanButton: HTMLButtonElement;
  statusEl: HTMLElement;
  videoEl: HTMLVideoElement;
  /** Appelé avec l'EAN lu ; c'est l'appelant qui décide quoi en faire (Open Food Facts). */
  surEan: (ean: string) => Promise<void> | void;
}

export async function startScan(elements: ScanElements): Promise<void> {
  const { scanButton, statusEl, videoEl, surEan } = elements;
  scanButton.disabled = true;

  if (!navigator.mediaDevices?.getUserMedia) {
    statusEl.textContent = "Ce navigateur n'expose pas la caméra (navigateur trop ancien, ou page hors HTTPS).";
    scanButton.disabled = false;
    return;
  }

  let stream: MediaStream;
  try {
    statusEl.textContent = 'Ouverture de la caméra…';
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
  } catch {
    statusEl.textContent = 'Accès à la caméra refusé ou indisponible.';
    scanButton.disabled = false;
    return;
  }

  videoEl.srcObject = stream;
  videoEl.hidden = false;
  await videoEl.play();

  statusEl.textContent = 'Préparation du lecteur de code-barres…';
  const scanner = await createBarcodeScanner();
  statusEl.textContent =
    scanner.engine === 'native'
      ? 'Recherche du code-barres…'
      : 'Recherche du code-barres (mode compatibilité, sans BarcodeDetector natif)…';

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  let stopped = false;
  const stop = (): void => {
    stopped = true;
    for (const track of stream.getTracks()) track.stop();
    videoEl.hidden = true;
    scanButton.disabled = false;
  };

  const SCAN_INTERVAL_MS = 350;
  const tick = async (): Promise<void> => {
    if (stopped) return;
    const ean = await detectOnce(scanner, videoEl, canvas, ctx);
    if (ean) {
      stop();
      statusEl.textContent = `Code-barres détecté : ${ean}`;
      await surEan(ean);
      return;
    }
    setTimeout(() => void tick(), SCAN_INTERVAL_MS);
  };
  void tick();
}

async function detectOnce(
  scanner: BarcodeScanner,
  videoEl: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D | null,
): Promise<string | null> {
  if (!ctx || videoEl.videoWidth === 0) return null;
  canvas.width = videoEl.videoWidth;
  canvas.height = videoEl.videoHeight;
  ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
  const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
  try {
    return await scanner.detect(frame);
  } catch {
    return null;
  }
}
